import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Modal,
  ModalProps,
  Row,
  TimePicker
} from 'antd'
import dayjs from 'dayjs'
import { TFunction } from 'i18next'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  useTreatmentServiceGetConsultationEndpoint,
  useTreatmentServiceGetConsultationListEndpointKey,
  useTreatmentServiceRescheduleConsultationEndpoint,
  useTreatmentServiceScheduleConsultationEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message, SaveConfirm, Text } from 'src/components'
import {
  dayMonthYearFormatWithDot,
  disabledPastDate,
  formatDate,
  hourMinutesSecondFormat,
  yearMonthDaySlash
} from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler, toDayjs } from 'src/utils'

type Props = {
  onCancel: () => void
  consultationId?: string
} & ModalProps

const getModalText = (hasId: boolean, t: TFunction) => {
  return {
    title: hasId
      ? t('consultations.reschedule_consultation')
      : t('consultations.schedule_new_consultation'),
    button: hasId ? t('roles.confirm') : t('persons.save')
  }
}

export const ScheduleConsultationModal = ({
  onCancel,
  consultationId,
  ...props
}: Props) => {
  const { t } = useLocales()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const { title, button } = getModalText(!!consultationId, t)

  const { data, isLoading } = useTreatmentServiceGetConsultationEndpoint(
    {
      id: String(consultationId)
    },
    [consultationId],
    { enabled: !!consultationId }
  )

  // const {data: persons} = usePersonsServiceGetInstitutionPersonListEndpoint({})

  const { mutate: schedule, isPending: isCreating } =
    useTreatmentServiceScheduleConsultationEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(t('consultations.consultation_scheduled_successfully'))
        onCancel()
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetConsultationListEndpointKey]
        })
      }
    })

  const { mutate: reschedule, isPending: isEditing } =
    useTreatmentServiceRescheduleConsultationEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors),
      onSuccess: () => {
        message.success(
          t('consultations.consultation_rescheduled_successfully')
        )
        onCancel()
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceGetConsultationListEndpointKey]
        })
      }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const payload = {
      personId: String(id),
      consultationDate: formatDate(values.consultationDate, yearMonthDaySlash),
      consultationHour: dayjs
        .utc(values.consultationHour)
        .format(hourMinutesSecondFormat)
    }

    consultationId
      ? reschedule({ requestBody: payload, id: consultationId })
      : schedule({ requestBody: payload })
  }

  useEffect(() => {
    if (consultationId && data) {
      form.setFieldsValue({
        consultationDate: toDayjs(data.generalInformation?.consultationDate),
        consultationHour: toDayjs(data.generalInformation?.consultationDate)
      })
    }
  }, [consultationId, data])

  return (
    <Modal
      open
      centered
      width={520}
      title={title}
      maskClosable={false}
      footer={null}
      loading={isLoading}
      onCancel={onCancel}
      {...props}
    >
      <Flex vertical gap={24}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Row gutter={[16, 24]}>
            <Col span={24}>
              <Text size="xl" weight={700}>
                {t('consultations.select_date_and_time')}
              </Text>
            </Col>

            <Col span={12}>
              <Form.Item
                name="consultationDate"
                label={t('consultations.consultation_date')}
                rules={requiredRules}
              >
                <DatePicker
                  format={dayMonthYearFormatWithDot}
                  className="full-width"
                  disabledDate={disabledPastDate}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="consultationHour"
                label={t('common.hour')}
                rules={requiredRules}
              >
                <TimePicker
                  showSecond={false}
                  needConfirm={false}
                  className="full-width"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Flex justify="center" gap={8}>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>

          <SaveConfirm
            title={`${button}?`}
            onConfirm={onSubmit}
            okButtonProps={{ loading: isCreating || isEditing }}
          >
            <Button type="primary">{button}</Button>
          </SaveConfirm>
        </Flex>
      </Flex>
    </Modal>
  )
}
